import { enqueueTask } from 'ember-concurrency';
import {
  getAllValuesForTasField,
  getSingleValueForTasField,
} from '../../../utils/tuition-assistance/fields.ts';
import { pluralize } from '@warp-drive/utilities/string';
import { saveRecord } from 'tio-data/requests/save-record';
import { service } from '@ember/service';
import Component from '@glimmer/component';
import AttachmentList from './upload-attachments/attachment-list.gts';
import TuitionAssistanceFormsUploadAttachmentsUploader from './upload-attachments/uploader.gts';
import type DigitalAssetsService from '../../../services/digital-assets.ts';
import type { Store as StoreService } from '@warp-drive/core';
import type { TasApplicationExtension as TasApplicationModel } from 'tio-data/data/resources/tas-application.ext';
import type { TasCourseExtension as TasCourseModel } from 'tio-data/data/resources/tas-course.ext';
import type { TasProgramInstanceExtension as TasProgramInstanceModel } from 'tio-data/data/resources/tas-program-instance.ext';
import type { TasProgramTemplateExtension as TasProgramTemplateModel } from 'tio-data/data/resources/tas-program-template.ext';
import type { TasAssetExtension as TasAssetModel } from 'tio-data/data/resources/tas-asset.ext';

export interface UploadAttachmentsSignature {
  Args: {
    attachable: TasProgramInstanceModel | TasCourseModel | TasApplicationModel;
    attachableType: string;
    programTemplate: TasProgramTemplateModel;
    classification: string;
    uploadRequired?: boolean;
    disabled?: boolean;
    useDropzone?: boolean;
    iconOnlyUpload?: boolean;
    hideInstructions?: boolean;
    hideAttachments?: boolean;
    instructions?: string;
    items?: string[];
    attachmentListItems?: TasAssetModel[];
    hideDiscarded?: boolean;
    didUploadFile?: () => void;
  };
  Element: HTMLDivElement;
}

export default class UploadAttachmentsComponent extends Component<UploadAttachmentsSignature> {
  /*
  COURSE_APPROVAL_DOCUMENTS - course approval
  COMPLETION_DOCUMENTS - course evidence
  PROGRAM_APPROVAL_DOCUMENTS - program approval
  */
  @service declare store: StoreService;
  @service declare digitalAssets: DigitalAssetsService;

  get instructionsField() {
    return `${this.args.classification}_STRING`;
  }

  get instructions() {
    return this.args.instructions
      ? this.args.instructions
      : // @ts-expect-error: not sure about this one
        getSingleValueForTasField(this.instructionsField, this.args.programTemplate?.fields) || '';
  }

  get attachmentItemsField() {
    return `${this.args.classification}_ATTACHMENT_FIELD`;
  }

  get attachmentItems() {
    return this.args.items
      ? this.args.items
      : // @ts-expect-error: not sure about this one
        getAllValuesForTasField(this.attachmentItemsField, this.args.programTemplate?.fields) || [];
  }

  get assetsForAttachmentList() {
    return this.args.attachmentListItems ?? this.args.attachable?.tasAssets ?? [];
  }

  didUploadFile = enqueueTask(async (file) => {
    const asset = this.digitalAssets.createAssetFromS3Details('tasAsset', file);

    asset.classification = this.args.classification;

    const attachable = this.args.attachable;
    const relationshipKey = pluralize(this.args.attachableType);

    if (attachable.isNew) {
      // @ts-expect-error: Dynamic relationship access on polymorphic model
      if (attachable.tasApplication?.isNew) {
        // @ts-expect-error: Dynamic relationship access on polymorphic model
        await saveRecord(this.store, attachable.tasApplication);
      }
      // @ts-expect-error: Dynamic relationship access on polymorphic model
      if (attachable.tasProgramInstance?.isNew) {
        // @ts-expect-error: Dynamic relationship access on polymorphic model
        await saveRecord(this.store, attachable.tasProgramInstance);
      }
      await saveRecord(this.store, attachable);
    }
    // @ts-expect-error: Dynamic relationship key access on polymorphic model
    asset[relationshipKey].push(attachable);
    // @ts-expect-error: Dynamic relationship access on polymorphic model
    attachable.tasAssets.push(asset);

    await saveRecord(this.store, asset);
    await saveRecord(this.store, attachable);

    if (this.args.didUploadFile) {
      this.args.didUploadFile();
    }
  });

  <template>
    <div class="tio-col-flush w-full" ...attributes>
      {{#unless @hideInstructions}}
        <p class="font-semibold">
          {{! @glint-expect-error: connected to the above ts-expect-error in instructions}}
          {{this.instructions}}
          {{#if @uploadRequired}}
            <span class="tio-fg-danger">*</span>
          {{/if}}
        </p>

        {{#if this.attachmentItems.length}}
          <ul class="font-medium tio-fg-secondary list-disc list-inside py-2">
            {{#each this.attachmentItems as |item|}}
              {{! @glint-expect-error: connected to the above ts-expect-error in attachmentItems}}
              <li class="px-4">{{item}}</li>
            {{/each}}
          </ul>
        {{/if}}
      {{/unless}}

      <TuitionAssistanceFormsUploadAttachmentsUploader
        @didUploadFile={{this.didUploadFile.perform}}
        @isProcessing={{this.didUploadFile.isRunning}}
        @disabled={{@disabled}}
        @useDropzone={{@useDropzone}}
        @iconOnlyUpload={{@iconOnlyUpload}}
      />
      {{#unless @hideAttachments}}
        <AttachmentList
          @assets={{this.assetsForAttachmentList}}
          @disabled={{@disabled}}
          @hideDiscarded={{@hideDiscarded}}
        />
      {{/unless}}
    </div>
  </template>
}
